import { useEffect, useId, useState, type FormEvent } from 'react';
import type {
  PartnerCodeAvailabilityDto,
  PartnerCodeChangeDto,
  PartnerCodeStatusDto,
} from '@washed/api-client';

type Problem = PartnerCodeAvailabilityDto['problem'] | 'tooSoon' | 'unavailable' | 'error';
const problemKey = (p: Exclude<Problem, 'tooSoon' | 'unavailable' | null>) =>
  p === 'error' ? 'error' : `custom${p}`;

/** Server refusals arrive as core.partner.code_<problem>. */
function refusal(code: string | undefined): Problem {
  const match = /^core\.partner\.code_(.+)$/u.exec(code ?? '');
  if (!match) return 'error';
  const p = match[1]!.toUpperCase();
  if (p === 'CHANGE_TOO_SOON') return 'tooSoon';
  if (p === 'NOT_PERSONALISABLE') return 'unavailable';
  return p as Problem;
}

/**
 * "Personnaliser mon code" on the Mon lien tab: the creator types the part
 * after INF-, sees the exact code and whether it is free as they type, then
 * confirms. The server decides every rule (partner-code.ts); this only shows it.
 */
export function CustomCode({
  codes,
  t,
  date,
  preview,
  onChanged,
  onStale,
}: {
  codes: PartnerCodeStatusDto;
  t: (key: string) => string;
  date: (iso: string) => string;
  preview: boolean;
  onChanged: (next: PartnerCodeChangeDto) => void;
  /** The server knows something newer (a supervisor just changed the code). */
  onStale: () => void;
}) {
  const id = useId();
  const [value, setValue] = useState(''),
    [check, setCheck] = useState<PartnerCodeAvailabilityDto | null>(null),
    [checking, setChecking] = useState(false),
    [problem, setProblem] = useState<Problem>(null),
    [busy, setBusy] = useState(false),
    [done, setDone] = useState(false);
  const typed = value.trim();
  useEffect(() => {
    setCheck(null);
    setProblem(null);
    if (!typed) return;
    setChecking(true);
    const controller = new AbortController();
    // Waits for a pause in typing so each keystroke is not a request.
    const timer = setTimeout(async () => {
      try {
        if (preview) {
          const code = `INF-${typed.toUpperCase().replace(/[\s-]/gu, '')}`;
          setCheck({
            code,
            available: code.length >= 7 && !/[01]/u.test(code),
            problem: /[01]/u.test(code)
              ? 'AMBIGUOUS_CHARACTERS'
              : code.length < 7
                ? 'TOO_SHORT'
                : null,
          });
          return;
        }
        const res = await fetch(`/v1/partner/code/availability?code=${encodeURIComponent(typed)}`, {
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(String(res.status));
        setCheck((await res.json()) as PartnerCodeAvailabilityDto);
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setProblem('error');
      } finally {
        if (!controller.signal.aborted) setChecking(false);
      }
    }, 350);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [typed]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!check?.available) return;
    setBusy(true);
    setProblem(null);
    try {
      let next: PartnerCodeChangeDto;
      if (preview) {
        next = {
          ...codes,
          changed: true,
          code: check.code,
          nextChangeAt: new Date(Date.now() + 30 * 86_400_000).toISOString(),
          previous: [
            {
              code: codes.code ?? '',
              validUntil: new Date(Date.now() + 90 * 86_400_000).toISOString(),
              endedBySupervisor: false,
            },
            ...codes.previous,
          ],
        };
      } else {
        const res = await fetch('/v1/partner/code', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Partner-CSRF': sessionStorage.getItem('partner.csrf') ?? '',
          },
          body: JSON.stringify({ code: typed }),
        });
        const body = (await res.json().catch(() => ({}))) as { code?: string };
        if (!res.ok) {
          const why = refusal(body.code);
          if (why === 'tooSoon' || why === 'unavailable') onStale();
          else setProblem(why);
          return;
        }
        next = body as PartnerCodeChangeDto;
      }
      setValue('');
      setDone(true);
      onChanged(next);
    } catch {
      setProblem('error');
    } finally {
      setBusy(false);
    }
  }

  const stillWorking = codes.previous.filter((p) => Date.parse(p.validUntil) > Date.now());
  const refused = problem === 'tooSoon' || problem === 'unavailable' ? null : problem;
  const shown = refused ?? (check && !check.available ? check.problem : null);
  return (
    <section className="section custom-code" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{t('customTitle')}</h2>
      <p className="section-intro">{t('customIntro')}</p>
      {stillWorking.length > 0 && (
        <ul className="previous-codes">
          {stillWorking.map((p) => (
            <li key={p.code}>
              {t('customPrevious')} <strong translate="no">{p.code}</strong> ·{' '}
              {t('customPreviousUntil')} {date(p.validUntil)}
            </li>
          ))}
        </ul>
      )}
      {done && (
        // Plain text, not a notice: the next-change notice follows and notices never stack.
        <p className="field-status done" role="status">
          {t('customDone')}
        </p>
      )}
      {!codes.personalisable ? (
        <p className="notice">{t('customUnavailable')}</p>
      ) : codes.nextChangeAt ? (
        <p className="notice">
          {t('customTooSoon')} {date(codes.nextChangeAt)}.
        </p>
      ) : (
        <form className="custom-code-form" onSubmit={(e) => void submit(e)} noValidate>
          <label htmlFor={`${id}-input`}>{t('customLabel')}</label>
          <div className="prefixed">
            <span aria-hidden="true" translate="no">
              INF-
            </span>
            <input
              id={`${id}-input`}
              value={value}
              onChange={(e) => setValue(e.target.value.toUpperCase().slice(0, 20))}
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={20}
              placeholder="KOFI228"
              aria-describedby={`${id}-hint ${id}-status`}
              aria-invalid={shown ? true : undefined}
            />
          </div>
          <p id={`${id}-hint`} className="caption">
            {t('customHint')}
          </p>
          <p
            id={`${id}-status`}
            className={`field-status${shown ? ' problem' : ''}`}
            role="status"
            aria-live="polite"
          >
            {checking
              ? t('customChecking')
              : shown
                ? t(problemKey(shown))
                : check?.available
                  ? `${t('customPreview')} ${check.code} · ${t('customAvailable')}`
                  : ''}
          </p>
          <button className="secondary" disabled={busy || checking || !check?.available}>
            {t('customSubmit')}
          </button>
        </form>
      )}
    </section>
  );
}
